import { FC, useState } from 'react'

import styled from 'styled-components'
import { IconTrash, IconGripVertical, IconPlus } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'

import { Modal } from 'src/Components/Modal'
import { Button } from 'src/Components/Button'
import { DatePicker } from 'src/Components/DatePicker/index.tsx'
import { Select } from 'src/Components/Select'
import {
  useSendForApprovalMutation,
  useSkipApprovalMutation,
} from 'src/Redux/api/documentsApi'
import { colors } from 'src/Helpers/constants'

const Stack = styled.div<{ $gap?: string }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $gap }) =>
    $gap === 'md' ? '16px' : $gap === 'sm' ? '8px' : '16px'};
`

const Group = styled.div<{ $justify?: string; $mt?: string }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  justify-content: ${({ $justify }) => $justify || 'flex-start'};
  margin-top: ${({ $mt }) => ($mt === 'md' ? '16px' : '0')};
`

const HintText = styled.span`
  font-size: 13px;
  color: ${colors.thirdGray};
  opacity: 0.7;
`

const OrderBadge = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${colors.fourthBlue};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  flex-shrink: 0;
`

const SelectWrapper = styled.div`
  flex: 1;
`

const DatePickerWrapper = styled.div`
  width: 180px;
  flex-shrink: 0;
`

const IconButton = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  color: ${colors.red};
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background-color: #ffe3e3;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const DraggableItem = styled.div<{ $isDragging: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: ${({ $isDragging }) => ($isDragging ? '#f0f0f0' : 'white')};
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  cursor: move;
  transition: all 0.2s;

  &:hover {
    background: #f8f8f8;
    border-color: #228be6;
  }
`

interface Approver {
  id: string
  userId: number | null
  userName: string | null
  deadline: Date | null
}

interface SendForApprovalModalProps {
  opened: boolean
  onClose: () => void
  contractId: number
  users: Array<{ id: number; name: string; email: string }>
  currentUserId?: number
}

export const SendForApprovalModal: FC<SendForApprovalModalProps> = ({
  opened,
  onClose,
  contractId,
  users,
  currentUserId,
}) => {
  const [approvers, setApprovers] = useState<Approver[]>([
    { id: '1', userId: null, userName: null, deadline: null },
  ])
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)

  // Исключаем текущего пользователя из списка согласователей
  const availableUsers = currentUserId
    ? users.filter((user) => user.id !== currentUserId)
    : users

  const [sendForApproval, { isLoading: isSending }] =
    useSendForApprovalMutation()
  const [skipApproval, { isLoading: isSkipping }] = useSkipApprovalMutation()

  const addApprover = (): void => {
    setApprovers([
      ...approvers,
      {
        id: Date.now().toString(),
        userId: null,
        userName: null,
        deadline: null,
      },
    ])
  }

  const removeApprover = (id: string): void => {
    if (approvers.length === 1) {
      notifications.show({
        title: 'Ошибка',
        message: 'Должен быть хотя бы один согласователь',
        color: 'red',
      })
      return
    }
    setApprovers(approvers.filter((a) => a.id !== id))
  }

  const updateApprover = (
    id: string,
    field: keyof Approver,
    value: number | string | Date | null,
  ): void => {
    setApprovers(
      approvers.map((a) => {
        if (a.id === id) {
          if (field === 'userId') {
            const userId = value ? parseInt(value as string) : null
            const user = availableUsers.find((u) => u.id === userId)
            return {
              ...a,
              userId,
              userName: user ? user.name : null,
            }
          }
          return { ...a, [field]: value }
        }
        return a
      }),
    )
  }

  const handleDragStart = (index: number): void => {
    setDraggedIndex(index)
  }

  const handleDragOver = (e: React.DragEvent, index: number): void => {
    e.preventDefault()
    if (draggedIndex === null || draggedIndex === index) return

    const newApprovers = [...approvers]
    const draggedItem = newApprovers[draggedIndex]
    newApprovers.splice(draggedIndex, 1)
    newApprovers.splice(index, 0, draggedItem)

    setApprovers(newApprovers)
    setDraggedIndex(index)
  }

  const handleDragEnd = (): void => {
    setDraggedIndex(null)
  }

  const handleSendForApproval = async (): Promise<void> => {
    const validApprovers = approvers.filter((a) => a.userId !== null)

    console.log(validApprovers)

    if (validApprovers.length === 0) {
      notifications.show({
        title: 'Ошибка',
        message: 'Выберите хотя бы одного согласователя',
        color: 'red',
      })
      return
    }

    try {
      await sendForApproval({
        contractId,
        data: {
          approvers: validApprovers.map((a) => ({
            userId: a.userId!,
            deadline: a.deadline
              ? a.deadline instanceof Date
                ? a.deadline.toISOString()
                : new Date(a.deadline).toISOString()
              : undefined,
          })),
        },
      }).unwrap()

      notifications.show({
        title: 'Успешно',
        message: 'Документ отправлен на согласование',
        color: 'green',
      })
      onClose()
    } catch (error) {
      console.error('Ошибка отправки на согласование:', error)
      notifications.show({
        title: 'Ошибка',
        message: 'Не удалось отправить документ на согласование',
        color: 'red',
      })
    }
  }

  const handleSkipApproval = async (): Promise<void> => {
    try {
      await skipApproval(contractId).unwrap()

      notifications.show({
        title: 'Успешно',
        message: 'Согласование пропущено, документ отмечен как согласованный',
        color: 'green',
      })
      onClose()
    } catch {
      notifications.show({
        title: 'Ошибка',
        message: 'Не удалось пропустить согласование',
        color: 'red',
      })
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title='Отправить на согласование'
      size='lg'
    >
      <Stack $gap='md'>
        <HintText>
          Добавьте согласователей и установите порядок согласования,
          перетаскивая карточки
        </HintText>

        <Stack $gap='sm'>
          {approvers.map((approver, index) => (
            <DraggableItem
              key={approver.id}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              $isDragging={draggedIndex === index}
            >
              <IconGripVertical size={20} color='#999' />
              <OrderBadge>{index + 1}</OrderBadge>

              <SelectWrapper>
                <Select
                  placeholder='Выберите согласователя'
                  value={approver.userId?.toString() || null}
                  onChange={(value) =>
                    updateApprover(approver.id, 'userId', value)
                  }
                  data={availableUsers.map((user) => ({
                    value: user.id.toString(),
                    label: `${user.name} (${user.email})`,
                  }))}
                  searchable
                  clearable
                />
              </SelectWrapper>

              <DatePickerWrapper>
                <DatePicker
                  placeholder='Дата согласования'
                  value={approver.deadline}
                  onChange={(value) =>
                    updateApprover(approver.id, 'deadline', value)
                  }
                  minDate={new Date()}
                  clearable
                />
              </DatePickerWrapper>

              <IconButton
                onClick={() => removeApprover(approver.id)}
                disabled={approvers.length === 1}
              >
                <IconTrash size={18} />
              </IconButton>
            </DraggableItem>
          ))}
        </Stack>

        <Button
          variant='light'
          leftSection={<IconPlus size={16} />}
          onClick={addApprover}
        >
          Добавить согласователя
        </Button>

        <Group $justify='space-between' $mt='md'>
          <Button
            variant='outline'
            color='gray'
            onClick={handleSkipApproval}
            disabled={isSkipping}
          >
            Согласование не требуется
          </Button>

          <Group>
            <Button variant='subtle' onClick={onClose}>
              Отмена
            </Button>
            <Button onClick={handleSendForApproval} disabled={isSending}>
              Отправить на согласование
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  )
}
